import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import { useLanguage } from "../context/LanguageContext";
import { useRef } from "react";

export function Testimonials() {
  const { t } = useLanguage();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 450;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  const testimonials = [
    {
      name: t('testimonials.list[0].name'),
      role: t('testimonials.list[0].role'),
      location: t('testimonials.list[0].location'),
      text: t('testimonials.list[0].text'),
      rating: 5
    },
    {
      name: t('testimonials.list[1].name'),
      role: t('testimonials.list[1].role'),
      location: t('testimonials.list[1].location'),
      text: t('testimonials.list[1].text'),
      rating: 5
    },
    {
      name: t('testimonials.list[2].name'),
      role: t('testimonials.list[2].role'),
      location: t('testimonials.list[2].location'),
      text: t('testimonials.list[2].text'),
      rating: 5
    },
    {
      name: t('testimonials.list[3].name'),
      role: t('testimonials.list[3].role'),
      location: t('testimonials.list[3].location'),
      text: t('testimonials.list[3].text'),
      rating: 5
    }
  ];

  return (
    <section className="py-32 px-6 lg:px-20 bg-gradient-to-b from-white via-[#DAF1DE]/20 to-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-20 right-10 w-96 h-96 bg-[#8EB69B]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#235347]/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-gradient-to-r from-[#8EB69B]/20 to-[#235347]/20 px-6 py-3 rounded-full border border-[#235347]/20 mb-6 backdrop-blur-sm"
          >
            <p className="text-[#163832] tracking-wider uppercase text-sm">Client Success Stories</p>
          </motion.div>
          <motion.h2 
            className="text-5xl lg:text-6xl text-[#051F20] mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('testimonials.title')}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('testimonials.subtitle')}
          </motion.p>
        </div>

        {/* Horizontal Scrolling Testimonials */}
        <div className="relative">
          {/* Scroll Left Button */}
          <motion.button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-md p-3 rounded-full shadow-2xl border border-[#235347]/20 hover:bg-[#235347] hover:text-white transition-all duration-300"
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft className="h-5 w-5" />
          </motion.button>

          {/* Scroll Right Button */}
          <motion.button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-md p-3 rounded-full shadow-2xl border border-[#235347]/20 hover:bg-[#235347] hover:text-white transition-all duration-300"
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight className="h-5 w-5" />
          </motion.button>

          {/* Horizontal Scroll Container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ 
                  y: -10,
                  scale: 1.02
                }}
                style={{ 
                  perspective: 1200,
                  transformStyle: 'preserve-3d'
                }}
                className="group relative bg-gradient-to-br from-white to-[#DAF1DE]/30 p-8 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#8EB69B]/50 hover:shadow-2xl transition-all duration-500 flex-shrink-0 w-96"
              >
                {/* 3D Depth Effect */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-[#8EB69B]/5 to-transparent rounded-2xl"
                  style={{ transform: 'translateZ(-10px)' }}
                />
                
                {/* Quote Icon */}
                <motion.div 
                  className="absolute -top-4 -left-4 bg-gradient-to-br from-[#235347] to-[#8EB69B] p-4 rounded-2xl shadow-2xl"
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <Quote className="h-6 w-6 text-white" />
                </motion.div>
                
                {/* Stars */}
                <div className="flex gap-1 mb-6 mt-4 relative" style={{ transform: 'translateZ(15px)' }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      whileHover={{ 
                        scale: 1.2,
                        y: -2
                      }}
                    >
                      <Star className="h-5 w-5 fill-[#C9A961] text-[#C9A961]" />
                    </motion.div>
                  ))}
                </div>
                
                {/* Testimonial Text */}
                <p className="text-gray-700 text-lg mb-8 leading-relaxed italic relative" style={{ transform: 'translateZ(10px)' }}>
                  "{testimonial.text}"
                </p>
                
                {/* Client Info */}
                <div className="relative" style={{ transform: 'translateZ(10px)' }}>
                  <h4 className="text-[#051F20] text-xl mb-1">{testimonial.name}</h4>
                  <p className="text-[#235347]">{testimonial.role}</p>
                  <p className="text-gray-500 text-sm">{testimonial.location}</p>
                </div>

                {/* Corner Accent */}
                <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-[#8EB69B]/20 group-hover:border-[#8EB69B]/60 rounded-br-3xl transition-colors duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}